import { db } from '../firebaseConfig.js';
import { collection, query, where, getDocs, setDoc, onSnapshot, getDocsFromCache, doc } from 'firebase/firestore';

// Get all votes for a solution and the current user's vote
export async function getSolutionVotes(solutionId, userId) {
  const lowQuota = process.env.REACT_APP_LOW_QUOTA_MODE === 'true';
  const votesRef = collection(db, 'solution_votes');
  const q = query(votesRef, where('solutionId', '==', solutionId));

  // Try cache first
  try {
    const cached = await getDocsFromCache(q);
    if (!cached.empty) {
      let yes = 0, no = 0, userVote = undefined;
      cached.forEach(docSnap => {
        const data = docSnap.data();
        if (data.voteType === 'yes') yes++;
        if (data.voteType === 'no') no++;
        if (data.userId === userId) userVote = data.voteType;
      });
      return { solutionId, yes, no, userVote };
    }
  } catch (_) { /* cache miss or unavailable */ }

  // In low quota mode, skip server read and return safe defaults
  if (lowQuota) {
    return { solutionId, yes: 0, no: 0, userVote: undefined };
  }

  // Fallback to server read; be resilient to quota errors
  let snapshot;
  try {
    snapshot = await getDocs(q);
  } catch (e) {
    console.warn('[getSolutionVotes] server read failed, returning defaults:', e?.code || e?.message || e);
    return { solutionId, yes: 0, no: 0, userVote: undefined };
  }

  let yes = 0, no = 0, userVote = undefined;
  snapshot.forEach(docSnap => {
    const data = docSnap.data();
    if (data.voteType === 'yes') yes++;
    if (data.voteType === 'no') no++;
    if (data.userId === userId) userVote = data.voteType;
  });

  return { solutionId, yes, no, userVote };
}

// Listen for live vote changes on a solution. Returns an unsubscribe function.
export function listenSolutionVotes(solutionId, userId, onUpdate) {
  const votesRef = collection(db, 'solution_votes');
  const q = query(votesRef, where('solutionId', '==', solutionId));
  return onSnapshot(q, (snapshot) => {
    let yes = 0, no = 0, userVote;
    snapshot.forEach(docSnap => {
      const data = docSnap.data();
      if (data.voteType === 'yes') yes++;
      if (data.voteType === 'no') no++;
      if (data.userId === userId) userVote = data.voteType;
    });
    onUpdate({ solutionId, yes, no, userVote });
  });
}

// Submit or update a user's vote
export async function submitSolutionVote(solutionId, userId, voteType) {
  try {
    console.log('Submitting vote:', { solutionId, userId, voteType });
    // Use deterministic ID to avoid read-before-write
    const voteDocRef = doc(db, 'solution_votes', `${solutionId}_${userId}`);
    await setDoc(voteDocRef, {
      solutionId,
      userId,
      voteType,
      timestamp: new Date()
    }, { merge: true });
    // Return updated counts; in low quota mode, avoid forcing a server read
    const updatedVotes = await getSolutionVotes(solutionId, userId);
    console.log('Vote submitted successfully:', updatedVotes);
    return updatedVotes;
  } catch (error) {
    console.error('Error in submitSolutionVote:', error);
    throw error; // Re-throw to be caught by the component
  }
}
